import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { confirmDialog } from "@/lib/confirm-dialog";
import {
  getPersonEquipment,
  saveEquipmentAsset,
  deleteEquipmentAsset,
  saveEquipmentLicense,
  deleteEquipmentLicense,
} from "@/lib/equipment.functions";
import {
  ASSET_CATEGORIES,
  ASSET_CATEGORY_LABELS,
  assetCategoryLabel,
  confirmationStatus,
  formatDateOrNull,
  type EquipmentAsset,
  type EquipmentLicense,
} from "@/lib/equipment";

export const Route = createFileRoute("/_admin/admin/ausstattung/$userId")({
  head: () => ({
    meta: [
      { title: "Ausstattung – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PersonEquipmentPage,
});

const inputCls =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function PersonEquipmentPage() {
  const { userId } = Route.useParams();
  const queryClient = useQueryClient();
  const loadFn = useServerFn(getPersonEquipment);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["equipment-person", userId],
    queryFn: () => loadFn({ data: { userId } }),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["equipment-person", userId] });
    queryClient.invalidateQueries({ queryKey: ["equipment-overview"] });
  }

  const saveAssetFn = useServerFn(saveEquipmentAsset);
  const deleteAssetFn = useServerFn(deleteEquipmentAsset);
  const saveLicenseFn = useServerFn(saveEquipmentLicense);
  const deleteLicenseFn = useServerFn(deleteEquipmentLicense);

  const assetMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => saveAssetFn({ data: input as any }),
    onSuccess: () => {
      toast.success("Gerät gespeichert.");
      setEditingAsset(null);
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message || "Speichern fehlgeschlagen."),
  });
  const deleteAssetMutation = useMutation({
    mutationFn: (id: string) => deleteAssetFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Gerät entfernt.");
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message || "Löschen fehlgeschlagen."),
  });
  const licenseMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => saveLicenseFn({ data: input as any }),
    onSuccess: () => {
      toast.success("Lizenz gespeichert.");
      setEditingLicense(null);
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message || "Speichern fehlgeschlagen."),
  });
  const deleteLicenseMutation = useMutation({
    mutationFn: (id: string) => deleteLicenseFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Lizenz entfernt.");
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message || "Löschen fehlgeschlagen."),
  });

  const [editingAsset, setEditingAsset] = useState<EquipmentAsset | "new" | null>(null);
  const [editingLicense, setEditingLicense] = useState<EquipmentLicense | "new" | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Wird geladen …
      </div>
    );
  }
  if (isError || !data) {
    return <div className="text-sm text-destructive">Ausstattung konnte nicht geladen werden.</div>;
  }

  async function handleDeleteAsset(asset: EquipmentAsset) {
    if (await confirmDialog({ title: "Gerät entfernen?", description: `„${asset.label}“ wird dauerhaft entfernt.` })) {
      deleteAssetMutation.mutate(asset.id);
    }
  }
  async function handleDeleteLicense(license: EquipmentLicense) {
    if (await confirmDialog({ title: "Lizenz entfernen?", description: `„${license.name}“ wird dauerhaft entfernt.` })) {
      deleteLicenseMutation.mutate(license.id);
    }
  }

  return (
    <div className="max-w-5xl">
      <Link to="/admin/ausstattung" className="text-xs font-semibold text-primary hover:underline">
        ← Ausstattung
      </Link>
      <header className="mb-8 mt-3">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-primary md:text-4xl">
          Ausstattung: {data.person.name}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {data.person.email ?? "Ohne E-Mail-Adresse"} · {data.person.activeAssetCount} aktive
          Geräte · {data.person.licenseCount} Lizenzen
        </p>
        <button
          type="button"
          onClick={async () => {
            await navigator.clipboard.writeText(`${window.location.origin}/admin/meine-ausstattung`);
            toast.success("Link kopiert – die Person bestätigt nach der Anmeldung.");
          }}
          className="mt-3 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-primary hover:bg-secondary"
        >
          Link zum Bestätigen kopieren
        </button>
      </header>

      <section className="mb-10">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-extrabold">Geräte und Ausstattung</h2>
          <button
            type="button"
            onClick={() => setEditingAsset("new")}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
          >
            <Plus className="h-3.5 w-3.5" /> Gerät hinzufügen
          </button>
        </div>
        {editingAsset ? (
          <AssetForm
            userId={userId}
            asset={editingAsset === "new" ? null : editingAsset}
            pending={assetMutation.isPending}
            onCancel={() => setEditingAsset(null)}
            onSubmit={(payload) => assetMutation.mutate(payload)}
          />
        ) : null}
        <div className="rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Art</th>
                <th className="px-4 py-3 font-semibold">Bezeichnung</th>
                <th className="px-4 py-3 font-semibold">Details</th>
                <th className="px-4 py-3 font-semibold">Ausgegeben</th>
                <th className="px-4 py-3 font-semibold">Zurück</th>
                <th className="px-4 py-3 font-semibold">Bestätigt am</th>
                <th className="px-4 py-3" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {data.assets.map((asset) => (
                <tr key={asset.id} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3">{assetCategoryLabel(asset.category)}</td>
                  <td className="px-4 py-3 font-semibold">
                    {asset.label}
                    {asset.returned_at ? (
                      <span className="ml-2 rounded bg-secondary px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                        zurückgegeben
                      </span>
                    ) : null}
                  </td>
                  <td className="max-w-[16rem] truncate px-4 py-3 text-muted-foreground">
                    {asset.details ?? "–"}
                  </td>
                  <td className="px-4 py-3 tabular-nums">{formatDateOrNull(asset.issued_at)}</td>
                  <td className="px-4 py-3 tabular-nums">{formatDateOrNull(asset.returned_at)}</td>
                  <td className="px-4 py-3 text-xs">
                    {(() => {
                      const s = confirmationStatus(asset);
                      if (s === "returned") return "–";
                      if (s === "open") return <span className="font-semibold text-destructive">offen</span>;
                      const date = formatDateOrNull(asset.confirmed_at!.slice(0, 10));
                      return s === "confirmed_with_note" ? (
                        <span title={asset.confirmation_note ?? ""}>
                          {date} · mit Hinweis: {asset.confirmation_note}
                        </span>
                      ) : (
                        date
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        aria-label={`Gerät ${asset.label} bearbeiten`}
                        onClick={() => setEditingAsset(asset)}
                        className="rounded-md p-1.5 hover:bg-secondary"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Gerät ${asset.label} entfernen`}
                        onClick={() => void handleDeleteAsset(asset)}
                        className="rounded-md p-1.5 text-destructive hover:bg-secondary"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.assets.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    Noch keine Geräte erfasst.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-10">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-extrabold">Lizenzen</h2>
          <button
            type="button"
            onClick={() => setEditingLicense("new")}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-secondary"
          >
            <Plus className="h-3.5 w-3.5" /> Lizenz hinzufügen
          </button>
        </div>
        {editingLicense ? (
          <LicenseForm
            userId={userId}
            license={editingLicense === "new" ? null : editingLicense}
            pending={licenseMutation.isPending}
            onCancel={() => setEditingLicense(null)}
            onSubmit={(payload) => licenseMutation.mutate(payload)}
          />
        ) : null}
        <div className="rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Lizenz</th>
                <th className="px-4 py-3 font-semibold">Anbieter</th>
                <th className="px-4 py-3 font-semibold">Geteilt mit</th>
                <th className="px-4 py-3 font-semibold">Ablauf</th>
                <th className="px-4 py-3 font-semibold">Verlängerung</th>
                <th className="px-4 py-3" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {data.licenses.map((license) => (
                <tr key={license.id} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-3 font-semibold">{license.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{license.vendor ?? "–"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{license.shared_with ?? "–"}</td>
                  <td className="px-4 py-3 tabular-nums">{formatDateOrNull(license.expires_at)}</td>
                  <td className="px-4 py-3">{license.auto_renews ? "automatisch" : "manuell"}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        aria-label={`Lizenz ${license.name} bearbeiten`}
                        onClick={() => setEditingLicense(license)}
                        className="rounded-md p-1.5 hover:bg-secondary"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Lizenz ${license.name} entfernen`}
                        onClick={() => void handleDeleteLicense(license)}
                        className="rounded-md p-1.5 text-destructive hover:bg-secondary"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.licenses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    Noch keine Lizenzen erfasst.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function AssetForm({
  userId,
  asset,
  pending,
  onCancel,
  onSubmit,
}: {
  userId: string;
  asset: EquipmentAsset | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}) {
  const [category, setCategory] = useState(asset?.category ?? "laptop");
  const [label, setLabel] = useState(asset?.label ?? "");
  const [details, setDetails] = useState(asset?.details ?? "");
  const [issuedAt, setIssuedAt] = useState(asset?.issued_at ?? todayIso());
  const [returnedAt, setReturnedAt] = useState(asset?.returned_at ?? "");

  function handle(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      ...(asset ? { id: asset.id } : {}),
      user_id: userId,
      category,
      label: label.trim(),
      details: details.trim() || null,
      issued_at: issuedAt,
      returned_at: returnedAt || null,
    });
  }

  return (
    <form onSubmit={handle} className="mb-4 rounded-lg border border-border bg-secondary/30 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-sm font-extrabold">
          {asset ? "Gerät bearbeiten" : "Neues Gerät"}
        </h3>
        <button type="button" onClick={onCancel} aria-label="Abbrechen" className="rounded-md p-1.5 hover:bg-secondary">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Art *</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} mt-1.5`}>
            {ASSET_CATEGORIES.map((c) => (
              <option key={c} value={c}>{ASSET_CATEGORY_LABELS[c]}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bezeichnung *</span>
          <input required value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120}
            placeholder="z. B. iPhone 15, Büro Schlüssel Bund" className={`${inputCls} mt-1.5`} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Details</span>
          <input value={details} onChange={(e) => setDetails(e.target.value)} maxLength={500}
            placeholder="z. B. Modell, Rufnummer, Seriennummer" className={`${inputCls} mt-1.5`} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ausgegeben am *</span>
          <input type="date" required value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} className={`${inputCls} mt-1.5`} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Zurückgegeben am</span>
          <input type="date" value={returnedAt} onChange={(e) => setReturnedAt(e.target.value)} className={`${inputCls} mt-1.5`} />
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel}
          className="rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-secondary">
          Abbrechen
        </button>
        <button type="submit" disabled={pending}
          className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
          {pending ? "Speichern …" : "Speichern"}
        </button>
      </div>
    </form>
  );
}

function LicenseForm({
  userId,
  license,
  pending,
  onCancel,
  onSubmit,
}: {
  userId: string;
  license: EquipmentLicense | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState(license?.name ?? "");
  const [vendor, setVendor] = useState(license?.vendor ?? "");
  const [seats, setSeats] = useState(license?.seats ?? 1);
  const [sharedWith, setSharedWith] = useState(license?.shared_with ?? "");
  const [startsAt, setStartsAt] = useState(license?.starts_at ?? todayIso());
  const [expiresAt, setExpiresAt] = useState(license?.expires_at ?? "");
  const [autoRenews, setAutoRenews] = useState(license?.auto_renews ?? false);

  function handle(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      ...(license ? { id: license.id } : {}),
      user_id: userId,
      name: name.trim(),
      vendor: vendor.trim() || null,
      seats,
      shared_with: sharedWith.trim() || null,
      starts_at: startsAt,
      expires_at: expiresAt || null,
      auto_renews: autoRenews,
    });
  }

  return (
    <form onSubmit={handle} className="mb-4 rounded-lg border border-border bg-secondary/30 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-sm font-extrabold">
          {license ? "Lizenz bearbeiten" : "Neue Lizenz"}
        </h3>
        <button type="button" onClick={onCancel} aria-label="Abbrechen" className="rounded-md p-1.5 hover:bg-secondary">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bezeichnung *</span>
          <input required value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
            placeholder="z. B. Adobe Creative Cloud" className={`${inputCls} mt-1.5`} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Anbieter</span>
          <input value={vendor} onChange={(e) => setVendor(e.target.value)} maxLength={120}
            placeholder="z. B. Adobe" className={`${inputCls} mt-1.5`} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Plätze</span>
          <input type="number" min={1} max={999} value={seats}
            onChange={(e) => setSeats(Math.max(1, Number(e.target.value) || 1))} className={`${inputCls} mt-1.5`} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Geteilt mit</span>
          <input value={sharedWith} onChange={(e) => setSharedWith(e.target.value)} maxLength={120}
            placeholder="z. B. Maria Muster (Adobe-Lizenzen teilen sich zwei Personen)" className={`${inputCls} mt-1.5`} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Beginn *</span>
          <input type="date" required value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className={`${inputCls} mt-1.5`} />
        </label>
        <label className="block">
          <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ablauf</span>
          <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className={`${inputCls} mt-1.5`} />
        </label>
        <label className="flex items-center gap-2 sm:col-span-2">
          <input type="checkbox" checked={autoRenews} onChange={(e) => setAutoRenews(e.target.checked)} className="h-4 w-4" />
          <span className="text-sm">Verlängert sich automatisch</span>
        </label>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel}
          className="rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-secondary">
          Abbrechen
        </button>
        <button type="submit" disabled={pending}
          className="rounded-md bg-brand-button px-4 py-2 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60">
          {pending ? "Speichern …" : "Speichern"}
        </button>
      </div>
    </form>
  );
}
